// quote-grid.js is similiar to policy-grid.js
/* But we keep quote-grid.js and policy-grid.js into two seperated files,
 * due to some actions and data between quotes and policies are handled differently.
 */
import { isEmpty, omit } from "lodash";
import { produce } from "immer";

import { getSearchResults, getSuggestResults } from "../promises/quotes";
import { getSearchResults as getOpenSearchResults } from "../promises/quote-policy-discovery";
import { getSuggestions as getOpenSearchSuggestions } from "../promises/quote-policy-suggest";
import shouldRenderSuggestions from "../utils/should-render-suggestions";
import {
  PRELOAD,
  LOADING,
  LOADED,
  LOAD_ERROR
} from "../static-data/loading-state-enum";
import debounce from "../utils/debounce";
import { FILTER_TYPES } from "../components/toolkit/filter/filters.constants";

import { hasFeature, pickEnvInfo, pickPolicyGrid } from "./selectors";

const PAGE_SIZE = 20;
const SUGGEST_PAGE_SIZE = 5;
const SUGGEST_DEBOUNCE_TIME = 300;

const RESULTS = "policy-grid/RESULTS";
const ERROR = "policy-grid/ERROR";
const SUGGESTIONS = "policy-grid/SUGGESTIONS";
const SEARCH_TEXT_CHANGE = "policy-grid/SEARCH_TEXT_CHANGE";
const SEARCH_TEXT_SUBMITTED = "policy-grid/SEARCH_TEXT_SUBMITTED";
const CHANGE_FILTER = "policy-grid/CHANGE_FILTER";
const RESET_FILTER = "policy-grid/RESET_FILTER";
const SORT_CHANGE = "policy-grid/SORT_CHANGE";
const PAGE_CHANGE = "policy-grid/PAGE_CHANGE";

export const setResults = ({ results, total, page }) => ({
  type: RESULTS,
  results,
  totalResultsCount: total,
  page,
  totalPages: Math.ceil(total / PAGE_SIZE)
});

const setError = errorMessage => ({
  type: ERROR,
  errorMessage
});

const setSuggestions = suggestions => ({
  type: SUGGESTIONS,
  suggestions
});

const searchTextSubmitted = text => ({
  type: SEARCH_TEXT_SUBMITTED,
  text
});

const searchTextChange = text => ({
  type: SEARCH_TEXT_CHANGE,
  text
});

const changeFilter = payload => ({
  type: CHANGE_FILTER,
  payload
});

const resetFilter = () => ({
  type: RESET_FILTER
});

const sortChange = payload => ({
  type: SORT_CHANGE,
  payload
});

const pageChange = page => ({
  type: PAGE_CHANGE,
  page
});

// searchIndex is being used as a priority queue index for updating the grid
// we're placing an index on every search request we fire and will only prioritize the most recent request.
// before we update the grid, we compare what the current searchIndex is.
// if it's greater than the searchIndex during the initialization of a request, we won't update the grid because another request has been fired.
let searchIndex = 0;
const searchQuotes = (dispatch, getState, page = 1) => {
  const state = getState();
  const hasOpenSearch = hasFeature(state, "open-search");
  const { username } = pickEnvInfo(state);
  const {
    submittedText,
    filters: {
      base: baseFilters,
      and: andFilters,
      or: orFilters,
      range: rangeFilters
    },
    sortField,
    sortDir
  } = pickPolicyGrid(state);

  // initialize a new request index
  searchIndex += 1;
  const thisSearchIndex = searchIndex;

  const defaultPayload = {
    ...baseFilters,
    userId: username,
    pageSize: PAGE_SIZE,
    from: (page - 1) * PAGE_SIZE,
    sortDir
  };

  if (hasOpenSearch) {
    const payload = {
      ...defaultPayload,
      ...(submittedText && { searchTerm: submittedText }),
      resultsetType: "compact",
      sortFields: sortField || "updateTimestamp",
      ...(!isEmpty(andFilters) && { filters: filterEmpty(andFilters) }),
      ...(!isEmpty(orFilters) && { orFilters: filterEmpty(orFilters) }),
      ...(!isEmpty(rangeFilters) && { rangeFilters: filterEmpty(rangeFilters) })
    };

    return getOpenSearchResults(payload)
      .then(
        response =>
          thisSearchIndex === searchIndex &&
          dispatch(
            setResults({
              results: response.hits,
              total: response.totalHits,
              page
            })
          )
      )
      .catch(
        error =>
          thisSearchIndex === searchIndex && dispatch(setError(error.message))
      );
  } else {
    const payload = {
      ...defaultPayload,
      searchTerm: submittedText,
      sortFields: sortField
        ? `InsurancePolicy.${sortField}`
        : "InsurancePolicy.updateTimestamp",
      pendingCancellation: andFilters.pendingCancellation || false,
      pendingNonRenewal: andFilters.pendingNonRenewal || false,
      pendingRenewal: andFilters.pendingRenewal || false,
      cancelled: andFilters.cancelled || false
    };

    return getSearchResults(filterEmpty(payload))
      .then(
        response =>
          thisSearchIndex === searchIndex &&
          dispatch(
            setResults({
              results: response.hits.hits,
              total: response.hits.total,
              page
            })
          )
      )
      .catch(
        error =>
          thisSearchIndex === searchIndex && dispatch(setError(error.message))
      );
  }
};

export const submitTextSearch = text => (dispatch, getState) => {
  dispatch(searchTextSubmitted(text));
  dispatch(setSuggestions([]));
  return searchQuotes(dispatch, getState);
};

export const submitAddFilter = payload => (dispatch, getState) => {
  const hasOpenSearch = hasFeature(getState(), "open-search");

  // OpenSearch ONLY
  // since we don't want multiple pill filters active at the same time
  // we're resetting the filters before activating any new filter
  hasOpenSearch &&
    payload.type !== FILTER_TYPES.BASE &&
    dispatch(resetFilter());

  const { filters } = pickPolicyGrid(getState());
  dispatch(
    changeFilter({
      type: payload.type,
      filters:
        payload.type === FILTER_TYPES.RANGE
          ? [...filters.range, ...payload.filter]
          : { ...filters[payload.type], ...payload.filter },
      range: payload.range
    })
  );

  dispatch(sortChange(payload));
  return searchQuotes(dispatch, getState);
};

export const submitRemoveFilter = payload => (dispatch, getState) => {
  const state = getState();
  const { filters } = pickPolicyGrid(state);
  const filteredFilters =
    payload.type === FILTER_TYPES.RANGE
      ? filters.range.filter(filter => filter.field !== payload.property)
      : omit(filters[payload.type], [payload.property]);

  dispatch(
    changeFilter({
      type: payload.type,
      filters: filteredFilters
    })
  );

  // resetting sorting after removing a filter
  dispatch(
    sortChange({
      sortField: "",
      sortDir: "desc"
    })
  );

  return searchQuotes(dispatch, getState);
};

export const submitResetFilter = () => (dispatch, getState) => {
  dispatch(resetFilter());
  return searchQuotes(dispatch, getState);
};

export const submitSortChange = sortField => (dispatch, getState) => {
  dispatch(sortChange(sortField));
  return searchQuotes(dispatch, getState);
};

export const submitPageChange = page => (dispatch, getState) => {
  dispatch(pageChange(page));
  return searchQuotes(dispatch, getState, page);
};

const buildSuggestionString = suggestion => {
  const {
    insuredDisplayName,
    propertyAddressLine1,
    propertyCity,
    propertyState,
    propertyZipCode
  } = suggestion;

  return `${insuredDisplayName}, ${propertyAddressLine1}, ${propertyCity}, ${propertyState} ${propertyZipCode}`;
};

const searchSuggestionsDebounced = debounce((dispatch, getState) => {
  const state = getState();
  const {
    searchText,
    suggestions,
    filters: {
      base: { showAll }
    }
  } = pickPolicyGrid(getState());
  const { username } = pickEnvInfo(state);
  const hasOpenSearch = hasFeature(state, "open-search");
  const hasDiscoveryV2 = hasFeature(state, "quote-discovery-filters-v2");

  if (shouldRenderSuggestions(searchText)) {
    if (hasOpenSearch) {
      if (hasDiscoveryV2) {
        // we're sending a search request here instead of suggestions if we have the FF on
        dispatch(submitTextSearch(searchText));
      } else {
        return getOpenSearchSuggestions({
          searchTerm: searchText,
          userId: username,
          showAll,
          resultSetType: "compact"
        }).then(response => {
          const {
            policyGrid: { searchText: currentSearchText }
          } = getState();

          // this is to ensure we update the suggestions for the most recent request
          if (searchText.toLowerCase() !== currentSearchText.toLowerCase()) {
            return;
          }

          if (response?.suggestions?.length) {
            // filter out quotes, limit and normalize output
            const normalizedSuggestions = response.suggestions
              .filter(suggestion => suggestion.policyId)
              .slice(0, SUGGEST_PAGE_SIZE)
              .map(suggestion => ({
                quoteId: suggestion.id,
                policyId: suggestion.policyId,
                text: buildSuggestionString(suggestion)
              }));

            return dispatch(setSuggestions(normalizedSuggestions));
          }

          return dispatch(setSuggestions([]));
        });
      }
    } else {
      return getSuggestResults({
        searchTerm: searchText,
        pageSize: SUGGEST_PAGE_SIZE,
        policies: true,
        quotes: false,
        showAll
      }).then(response => {
        const {
          policyGrid: { searchText: currentSearchText }
        } = getState();

        // this is to ensure we update the suggestions for the most recent request
        if (searchText.toLowerCase() !== currentSearchText.toLowerCase()) {
          return;
        }

        if (response?.suggestions?.length) {
          // filter out quotes and normalize output
          const normalizedSuggestions = response.suggestions[0].options
            .filter(option => option.payload.policyID)
            .map(option => ({
              quoteId: option.payload.id,
              policyId: option.payload.policyID,
              text: option.text
            }));

          return dispatch(setSuggestions(normalizedSuggestions));
        }
        return dispatch(setSuggestions([]));
      });
    }
  } else {
    if (suggestions.length > 0) {
      dispatch(setSuggestions([]));
    }
  }
}, SUGGEST_DEBOUNCE_TIME);

export const updateSearchText = text => (dispatch, getState) => {
  dispatch(searchTextChange(text));
  searchSuggestionsDebounced(dispatch, getState);
  const { submittedText } = pickPolicyGrid(getState());

  // when the grid is already populated with a result set from a previous submittedText and the user clears the search term
  // we want to send a search request without any search term and reset the grid
  if (!text && submittedText) {
    dispatch(submitTextSearch(""));
  }
};

const initState = {
  loadingState: PRELOAD,
  results: [],
  totalResultsCount: 0,
  errorMessage: null,
  suggestions: [],
  searchText: "",
  submittedText: "",
  filters: {
    base: {
      quotes: false,
      policies: true,
      showAll: true
    },
    and: {},
    or: {},
    range: []
  },
  sortField: "",
  sortDir: "desc",
  page: 1,
  totalPages: 1
};

export const policyGrid = produce((draft, action) => {
  switch (action.type) {
    case RESET_FILTER: {
      return {
        ...initState,
        filters: { ...initState.filters, base: { ...draft.filters.base } }
      };
    }
    case RESULTS:
      draft.results = action.results;
      draft.totalResultsCount = action.totalResultsCount;
      draft.loadingState = LOADED;
      draft.errorMessage = null;
      draft.page = action.page;
      draft.totalPages = action.totalPages;
      return;

    case ERROR:
      draft.errorMessage = action.errorMessage;
      draft.loadingState = LOAD_ERROR;
      draft.results = [];
      draft.totalResultsCount = 0;
      draft.page = 1;
      draft.totalPages = 0;
      return;

    case SUGGESTIONS:
      draft.suggestions = action.suggestions;
      return;

    case SEARCH_TEXT_CHANGE:
      draft.searchText = action.text;
      return;

    case SEARCH_TEXT_SUBMITTED:
      draft.submittedText = action.text;
      draft.loadingState = LOADING;
      return;

    case CHANGE_FILTER:
      draft.filters[action.payload.type] = action.payload.filters;
      if (action.payload?.range && action.payload.range.length > 0) {
        draft.filters.range = action.payload.range;
      }
      draft.loadingState = LOADING;
      return;

    case SORT_CHANGE:
      draft.sortField = action.payload.sortField;
      draft.sortDir = action.payload.sortDir;
      draft.loadingState = LOADING;
      return;

    case PAGE_CHANGE:
      draft.page = action.page;
      draft.loadingState = LOADING;
      return;
  }
}, initState);

export function filterEmpty(data) {
  if (Array.isArray(data)) {
    return data.filter(item => item.value !== "");
  } else {
    const r = {};
    Object.keys(data).forEach(key => {
      let val = data[key];
      if (typeof val === "string") {
        val = val.trim();
      }
      if (typeof val === "undefined" || val === null || val === "") return;
      r[key] = val;
    });
    return r;
  }
}
